import { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Container,
	Form,
	Modal,
	Offcanvas,
	OverlayTrigger,
	Spinner,
	Stack,
	Tooltip
} from 'react-bootstrap';
import { BoxArrowRight, Envelope, House, InfoCircle, Person, QuestionCircle, X } from 'react-bootstrap-icons';
import { useAuth0 } from '@auth0/auth0-react';
import {  useAuthData } from '../LoginContext';
import { jwtDecode } from 'jwt-decode';
import { useTranslationData } from '../TranslationContext';
import { getSupportedBrowserDefaultConsumerLocale } from '../util/BrowserLanguageUtil';

const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

const NavigationBar = () => {
	const { logout } = useAuth0();
	const { auth0IdToken, selectedConsumer, setSelectedConsumer, setSelectedConsumerId, setRnResponseProjectId, consumerToggleOptions } = useAuthData();
	const { initLanguages, setTextBefore, setTextAfter, setLanguageFrom, setLanguageTo, setIsSettingsOpen, isSettingsOpen } = useTranslationData();

	const [showSsoLogoutModal, setShowSsoLogoutModal] = useState(false)
	const [isLoading, setIsLoading] = useState(false);
	const [showConsumerToggleConfirmation, setShowConsumerToggleConfirmation] = useState(false);

	const [consumer, setConsumer] = useState<string>();

	useEffect(() => {
		if (!isSettingsOpen) {
			setShowConsumerToggleConfirmation(false)
		}
	}, [isSettingsOpen])

	useEffect(() => {
		if (selectedConsumer) {
			setConsumer(selectedConsumer)
		}
	}, [selectedConsumer]);

	const reset = async (consumerId: string) => {
		// When switching consumers, reset the RnResponseProjectId because it is associated with the consumer.
		setRnResponseProjectId(null)

		// reset source text and translated textarea
		setTextBefore('');
		setTextAfter('');

		// reset to default 'Detect Language' select option
		setLanguageFrom('');

		// reset to default browser locale select option
		const languages = await initLanguages(auth0IdToken, consumerId);
		const defaultConsumerLocale = getSupportedBrowserDefaultConsumerLocale(languages);
		setLanguageTo(defaultConsumerLocale)
	}

	const handleConsumerSelect = async (e: any) => {
		const selected = consumerToggleOptions?.find(i => i.name === e.target.value);
		if (selected) {
			setIsLoading(true);
			setShowConsumerToggleConfirmation(false);
			setSelectedConsumer(selected.name);
			setSelectedConsumerId(selected.id);
			setShowConsumerToggleConfirmation(true);
			await reset(selected.id);
		}
		setIsLoading(false);
	};

	const getAuth0IdFromToken = (token: any) => {
		try{
			const decodedToken = jwtDecode(token)
			return decodedToken.sub
		} catch (e) {
			console.error("Error decoding auth0 token:" , e)
			return null;
		}
	}

	const handleLogout = () => {
		const auth0Id = getAuth0IdFromToken(auth0IdToken)
		if (!auth0Id?.startsWith('auth0')){
			setShowSsoLogoutModal(true)
		}
		else{
			logout({
				clientId: clientId,
				logoutParams: {
					returnTo: window.location.origin
				}
			})
		}
	}

	const handleModalClose = () => setShowSsoLogoutModal(false);

	const handleModalConfirm = () => {
		setShowSsoLogoutModal(false);
		logout({
			clientId: clientId,
			logoutParams: {
				returnTo: window.location.origin
			}
		})
	}

	return (
			<Container>
				<Offcanvas
					className='navigation-off-canvas'
					show={isSettingsOpen}
					onHide={() => setIsSettingsOpen(false)}
					placement='end'
				>
					<Offcanvas.Header className="mb-1">
						<Button variant='brand' onClick={() => setIsSettingsOpen(false)} aria-label='Close' className='nav-close-btn'>
							<X size={40}/>
						</Button>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<Stack className='h-100' direction='vertical'>
							<Stack direction='vertical' gap={2} className='mt-1'>
								{ consumerToggleOptions !== undefined && consumerToggleOptions.length > 1 && (
									<>
									<Form.Label className='consumer-toggle-label'>Translation Profile
										<OverlayTrigger
              								placement="bottom"
              								overlay={<Tooltip id="consumerSelect-tooltip">Translation Profiles may have unique sets of languages and glossary terms available for translation. </Tooltip>}
										>
              								<InfoCircle className="ms-2 translation-profile-info-circle" />
            							</OverlayTrigger>
									</Form.Label>
									<Form.Select id='consumerToggle' aria-label="Default select example" value={consumer} onChange={handleConsumerSelect}>
										{consumerToggleOptions.map(option => (
											<option key={option.id} value={option.name}>{option.name}</option>
										))}
									</Form.Select>
									<div className='consumer-toggle-spinner-container'>
										{ isLoading && <Spinner className='spinner-custom-blue' animation='border'></Spinner> }
									</div>
									<div>
										{ showConsumerToggleConfirmation && <Alert variant={'info'} dismissible={true} onClose={() => setShowConsumerToggleConfirmation(false)}>Languages and glossary terms for this profile are loaded and will be applied to new translations.</Alert> }
									</div>
									</>
								)}
								<Button
									size="lg"
									variant="nav"
									type='submit'
									onClick={() =>
										window.open(
											process.env.REACT_APP_BASE_URL + '/linguistnow/portal/index.html#/account'
										)
									}
								>
									<Person className='me-4 mb-1 icon' />
									Account
								</Button>
								<Button
									size='lg'
									variant='nav'
									onClick={() => {
										window.open('https://languageio.com/contact/', '_blank');
										setIsSettingsOpen(false);
									}}
								>
									<Envelope className='me-4 mb-1 icon' />
									Contact Us
								</Button>
								<Button
									size='lg'
									variant='nav'
									onClick={() => {
										window.open('https://languageio.com', '_blank');
										setIsSettingsOpen(false);
									}}
								>
									<House className='me-4 mb-1 icon' />
									About Us
								</Button>
								<Button
									size='lg'
									variant='nav'
									onClick={() => {
										window.open(
											'https://languageio.zendesk.com/hc/en-us',
											'_blank'
										);
										setIsSettingsOpen(false);
									}}
								>
									<QuestionCircle className='me-4 mb-1 icon' />
									Help Center
								</Button>
								<Button size="lg" variant="nav"
										onClick={() => handleLogout()}>
									<BoxArrowRight className='me-4 mb-1 icon' />
									Sign out
								</Button>
								<Modal show={showSsoLogoutModal} onHide={handleModalClose}>
        							<Modal.Header closeButton>
          								<Modal.Title>A note for SSO users</Modal.Title>
       								 </Modal.Header>
        								<Modal.Body>This will log you out of your Language I/O account. However, you are still logged in to your Single Sign-On (SSO) account. To switch users in Language I/O, please log out of your SSO account first (directly on the SSO website), and then log in to Language I/O again with the desired user credentials.</Modal.Body>
        								<Modal.Footer>
          									<Button variant="primary" onClick={handleModalConfirm}>
            									Ok, logout.
          									</Button>
        								</Modal.Footer>
      							</Modal>
							</Stack>
							<div className='mt-auto mx-auto'>v1.4.0</div>
						</Stack>
					</Offcanvas.Body>
				</Offcanvas>
			</Container>
	);
};

export default NavigationBar;
